/**
 * dview:BranchViewer: a switch's legs as a small drawing, picked by its type (dview:glyphBy, dview:glyph):
 * a fork (a Y), curved (a Y with both legs turning) or a cross (an X with its slips). The row lists the
 * branches; each names the net elements of its relation (columns a and b) and what lies on them (a-owner,
 * b-owner). The names sit in their own columns beside the drawing, one line each, so they never overlap.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { pickLang, setting, settingList } from '@utils/viewShapes.utils';
import { keyedValues } from '../page';
import { trackAmong } from './topology';
import { recordName } from './values';
import type { Widget, WidgetContext } from './types';
import styles from './branches.module.scss';

const RAILML = 'http://ontology.railml.org/railml3#';
const TRACK = `${RAILML}Track`;
const W = 240;
// Between two names, and above the top and below the bottom one
const STEP = 44;
const EDGE = 22;

type Glyph = 'fork' | 'curved' | 'cross';
type Item = { iri: string; cells: Record<string, { value: string }[] | undefined> };

interface Leg {
  iri: string;
  kind: string;
  a: string | null;
  b: string | null;
  aTrack: string | null;
  bTrack: string | null;
}

interface Label {
  y: number;
  track: string | null;
  note?: string;
  key: string;
}

const legOf = (item: Item): Leg => ({
  iri: item.iri,
  kind: (item.cells.kind ?? []).map((value) => value.value).find((iri) => iri.startsWith(RAILML))?.slice(RAILML.length) ?? '',
  a: item.cells.a?.[0]?.value ?? null,
  b: item.cells.b?.[0]?.value ?? null,
  aTrack: trackAmong(item.cells['a-owner']),
  bTrack: trackAmong(item.cells['b-owner']),
});

const Names = ({ labels, height, context, side }: { labels: Label[]; height: number; context: WidgetContext; side: 'left' | 'right' }) => {
  const { t } = useTranslation();
  const trackShape = context.model?.shapes?.nodeShapes.find((node) => node.targetClasses.includes(TRACK));
  const trackClass = trackShape ? pickLang(trackShape.label, context.language) : null;
  return (
    <div className={styles.names} data-side={side} style={{ blockSize: height }}>
      {labels.map((label) => {
        const name = label.track ? recordName(label.track, context, trackClass) : null;
        return (
          <span key={label.key} className={styles.name} style={{ top: label.y }} title={name ?? undefined}>
            {label.track ? <Link to={uriToDereferencePath(label.track)}>{name}</Link> : <span className={styles.note}>{t('entity.template.branches.unknown')}</span>}
            {label.note && <span className={styles.note}>{label.note}</span>}
          </span>
        );
      })}
    </div>
  );
};

/** A Y: where it comes from on the left, each leg's end on the right; straight legs on the line unless curved. */
function Fork({ legs, own, straight, curved, context }: { legs: Leg[]; own: Set<string>; straight: string | undefined; curved: boolean; context: WidgetContext }) {
  const { t } = useTranslation();
  // Where a leg comes from: the switch's own net element, else the element the legs share
  const seen = new Map<string, number>();
  for (const leg of legs) for (const end of [leg.a, leg.b]) if (end) seen.set(end, (seen.get(end) ?? 0) + 1);
  let trunk: string | null = null;
  const ends = legs.map((leg) => {
    const ownA = Boolean(leg.a && own.has(leg.a));
    const ownB = Boolean(leg.b && own.has(leg.b));
    const fromB = ownB && !ownA ? true : ownA ? false : (seen.get(leg.b ?? '') ?? 0) > (seen.get(leg.a ?? '') ?? 0);
    trunk ??= fromB ? leg.bTrack : leg.aTrack;
    const course = leg.kind === 'LeftBranch' ? 'left' : leg.kind === 'RightBranch' ? 'right' : null;
    const side = !curved && (leg.kind === 'StraightBranch' || (course !== null && course === straight)) ? 'straight' : course === 'left' ? 'left' : 'right';
    return { iri: leg.iri, side, track: fromB ? leg.aTrack : leg.bTrack };
  });
  // The first straight leg on the line, left legs above it, the others below, one name apart
  const onLine = ends.find((end) => end.side === 'straight');
  const above = ends.filter((end) => end.side === 'left');
  const below = ends.filter((end) => end !== onLine && end.side !== 'left');
  const mid = EDGE + above.length * STEP;
  const height = mid + below.length * STEP + EDGE;
  const placed = [
    ...(onLine ? [{ ...onLine, y: mid }] : []),
    ...above.map((end, index) => ({ ...end, y: mid - STEP * (index + 1) })),
    ...below.map((end, index) => ({ ...end, y: mid + STEP * (index + 1) })),
  ];
  const point = 80;
  return (
    <>
      <Names labels={[{ key: 'trunk', y: mid, track: trunk }]} height={height} context={context} side="left" />
      <svg width={W} height={height} viewBox={`0 0 ${W} ${height}`} style={{ blockSize: height }} className={styles.drawing} preserveAspectRatio="none" aria-hidden="true">
        <path d={`M0 ${mid}H${point}`} className={styles.trunk} />
        {placed.map((end) => (
          <path
            key={end.iri}
            d={
              end.y === mid
                ? `M${point} ${mid}H${W}`
                : curved
                  ? `M${point} ${mid}C${point + 70} ${mid} ${point + 80} ${end.y} ${W} ${end.y}`
                  : `M${point} ${mid}L${point + 70} ${end.y}H${W}`
            }
            className={end.side === 'straight' ? styles.straight : styles.branch}
          />
        ))}
        <rect x={point - 8} y={mid - 8} width={16} height={16} transform={`rotate(45 ${point} ${mid})`} className={styles.point} />
      </svg>
      <Names
        labels={placed.map((end) => ({ key: end.iri, y: end.y, track: end.track, note: t(`entity.template.branches.${end.side}`) }))}
        height={height}
        context={context}
        side="right"
      />
    </>
  );
}

/**
 * An X: the two straight legs cross; a slip joins the two ends on one side of the crossing. Each end sits at
 * its corner: the first straight leg runs top left to bottom right, and a slip's ends share a side.
 */
function Cross({ legs, context }: { legs: Leg[]; context: WidgetContext }) {
  const straights = legs.filter((leg) => leg.kind === 'StraightBranch');
  const slips = legs.filter((leg) => leg.kind !== 'StraightBranch');
  const track = new Map<string, string | null>();
  for (const leg of legs) {
    if (leg.a) track.set(leg.a, leg.aTrack);
    if (leg.b) track.set(leg.b, leg.bTrack);
  }
  const joined = (x: string | null, y: string | null) => Boolean(x && y) && slips.some((slip) => (slip.a === x && slip.b === y) || (slip.a === y && slip.b === x));
  const [first, second] = straights;
  const l1 = first.a;
  const r2 = first.b;
  // The second straight leg's end a slip joins to l1 (or r2's partner on the left) lies top right
  const flip = joined(l1, second.a) || joined(r2, second.b);
  const r1 = flip ? second.a : second.b;
  const l2 = flip ? second.b : second.a;
  const height = EDGE * 2 + STEP * 2;
  const top = EDGE + 6;
  const bottom = height - EDGE - 6;
  const x0 = 4;
  const x1 = W - 4;
  const label = (end: string | null, y: number, key: string): Label => ({ key, y, track: end ? (track.get(end) ?? null) : null });
  return (
    <>
      <Names labels={[label(l1, top, 'l1'), label(l2, bottom, 'l2')]} height={height} context={context} side="left" />
      <svg width={W} height={height} viewBox={`0 0 ${W} ${height}`} style={{ blockSize: height }} className={styles.drawing} preserveAspectRatio="none" aria-hidden="true">
        <path d={`M${x0} ${top}L${x1} ${bottom}`} className={styles.straight} />
        <path d={`M${x0} ${bottom}L${x1} ${top}`} className={styles.straight} />
        {joined(l1, r1) && <path d={`M${x0} ${top}C${W / 2} ${top + 36} ${W / 2} ${top + 36} ${x1} ${top}`} className={styles.branch} />}
        {joined(l2, r2) && <path d={`M${x0} ${bottom}C${W / 2} ${bottom - 36} ${W / 2} ${bottom - 36} ${x1} ${bottom}`} className={styles.branch} />}
      </svg>
      <Names labels={[label(r1, top, 'r1'), label(r2, bottom, 'r2')]} height={height} context={context} side="right" />
    </>
  );
}

const Branches = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const model = context.model;
  const items = model?.tables[property.id]?.items ?? [];
  if (!model || items.length === 0) return null;
  const legs = items.map(legOf);
  const type = keyedValues(model, setting(property, 'glyphBy') ?? '').map((value) => value.value);
  const glyphs = new Map(settingList(property, 'glyph').map((entry) => entry.split('=') as [string, Glyph]));
  const known = type.map((value) => glyphs.get(value)).find(Boolean);
  // A cross needs its two straight legs; with fewer it is drawn as a fork
  const glyph: Glyph = known === 'cross' && legs.filter((leg) => leg.kind === 'StraightBranch').length < 2 ? 'fork' : (known ?? 'fork');
  const own = new Set(keyedValues(model, setting(property, 'topology') ?? '').map((value) => value.value));
  const straight = keyedValues(model, setting(property, 'straight') ?? '')[0]?.value;
  return (
    <figure className={styles.fork} aria-label={pickLang(property.name, context.language) ?? undefined}>
      <div className={styles.layout}>
        {glyph === 'cross' ? (
          <Cross legs={legs} context={context} />
        ) : (
          <Fork legs={legs} own={own} straight={straight} curved={glyph === 'curved'} context={context} />
        )}
      </div>
      {!known && <figcaption className={styles.note}>{t('entity.template.branches.notDrawn')}</figcaption>}
    </figure>
  );
};

export const BranchViewer: Widget = (props) => <Branches {...props} />;
